import {Editor, Extension} from '@tiptap/core';
import {
  insertText,
  isFormatActive,
  linkAt,
  removeFormat,
  setLink,
  setStyleProperty,
  StyleProperty,
  styleOf,
  toggleFormat,
  toggleLinkUnderline,
  ToggleFormat,
  unsetLink,
} from './rich-text-commands';
import {canonicalHtml, escapeHtmlText, sameHtml} from './rich-text-html';
import {richTextExtensions, RichTextShape} from './rich-text-schema';
import {inline, serializeHtml, throughSchema} from './rich-text-serialize';

export interface RichTextEditorOptions {
  element: HTMLElement;
  shape: RichTextShape;
  html?: string;
  editable?: boolean;
  /** Bold, italic, underline and strike-through as span styles (the widget texts), not tags. */
  inlineStyles?: boolean;
  /** Underline on a link switches the link's own underline (the widget texts). */
  linkUnderlineToggle?: boolean;
  /** Plain text: no formatting, pasted text loses its formatting. */
  plainText?: boolean;
  /** In plain text, whether Enter writes a line break (else it does nothing). */
  multiline?: boolean;
  /** Ctrl+L, for the host to open its link dialog. */
  onLinkShortcut?: () => void;
  /** The text changed by an edit (not by setHtml). */
  onChange?: (html: string) => void;
  /** The selection or the formatting at the cursor changed. */
  onState?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * The panel's rich-text editor, without a user interface (sb-rich-text draws the toolbar; the
 * influencer profile page's bundle draws its own): TipTap on the schema of rich-text-schema, with
 * CKEditor's keys (Enter as a line break in an inline text, Ctrl+B/I/U, Ctrl+L) and its output.
 *
 * `html()` is the text as the editor holds it; an inline text is written without a wrapper, an
 * empty one as ''. `keeps(html)` says whether loading `html` and writing it back gives the same
 * HTML (rich-text-html's sameHtml): a host shows a text that does not as HTML source instead, so
 * that nothing it holds is lost by opening it.
 */
export class RichTextEditor {
  readonly editor: Editor;
  private options: RichTextEditorOptions;
  private settingHtml = false;

  constructor(options: RichTextEditorOptions) {
    this.options = {editable: true, inlineStyles: false, multiline: true, ...options};
    const host = this;
    const Keys = Extension.create({
      name: 'snoballKeys',
      priority: 1000,
      addKeyboardShortcuts() {
        return {
          'Enter': () => host.lineBreak(),
          'Shift-Enter': () => host.lineBreak(true),
          'Mod-b': () => host.toggle('bold'),
          'Mod-i': () => host.toggle('italic'),
          'Mod-u': () => host.toggle('underline'),
          'Mod-l': () => {
            host.options.onLinkShortcut?.();
            return !!host.options.onLinkShortcut;
          },
        };
      },
    });
    this.editor = new Editor({
      element: this.options.element,
      extensions: [...richTextExtensions(this.options.shape), Keys],
      content: this.options.html || '',
      editable: this.options.editable,
      enableInputRules: false,
      enablePasteRules: false,
      editorProps: {
        transformPastedHTML: (html) => this.pastedHtml(html),
        handlePaste: (_view, event) => this.pastePlainText(event),
      },
      onUpdate: () => {
        if (!this.settingHtml) {
          this.options.onChange?.(this.html());
        }
        this.options.onState?.();
      },
      onSelectionUpdate: () => this.options.onState?.(),
      onTransaction: () => this.options.onState?.(),
      onFocus: () => this.options.onFocus?.(),
      onBlur: () => this.options.onBlur?.(),
    });
  }

  get shape(): RichTextShape {
    return this.options.shape;
  }

  get plainText(): boolean {
    return !!this.options.plainText;
  }

  html(): string {
    const {doc} = this.editor.state;
    if (doc.isTextblock) {
      return inline(doc.content);
    }
    const html = serializeHtml(doc.content);
    return html === '<p></p>' ? '' : html;
  }

  /** Replaces the text without reporting it as an edit, and without an undo step to it. */
  setHtml(html: string): void {
    if ((html || '') === this.html()) {
      return;
    }
    this.settingHtml = true;
    try {
      this.editor.chain().setMeta('addToHistory', false).setContent(html || '', {emitUpdate: true}).run();
    } finally {
      this.settingHtml = false;
    }
  }

  keeps(html: string): boolean {
    // An empty text (a new form's field) has nothing to lose: the editor holds it as an empty paragraph.
    if (!canonicalHtml(html || '', this.options.shape)) {
      return true;
    }
    return sameHtml(html || '', throughSchema(html, this.editor.schema), this.options.shape);
  }

  setEditable(editable: boolean): void {
    this.options.editable = editable;
    this.editor.setEditable(editable, false);
  }

  setPlainText(plainText: boolean, multiline = this.options.multiline): void {
    this.options.plainText = plainText;
    this.options.multiline = multiline;
    this.options.onState?.();
  }

  focus(): void {
    this.editor.commands.focus();
  }

  isActive(format: ToggleFormat): boolean {
    return isFormatActive(this.editor.state, format);
  }

  styleAtSelection(property: StyleProperty): string | null {
    const {state} = this.editor;
    return styleOf(state.storedMarks || state.selection.$from.marks(), property);
  }

  link(): {href: string; target: string | null} | null {
    const found = linkAt(this.editor.state);
    if (!found) {
      return null;
    }
    const attributes = found.mark.attrs['attributes'] || {};
    return {href: attributes['href'] || '', target: attributes['target'] || null};
  }

  hasSelection(): boolean {
    return !this.editor.state.selection.empty;
  }

  toggle(format: ToggleFormat): boolean {
    if (!this.formattable()) {
      return true;
    }
    if (format === 'underline' && this.options.linkUnderlineToggle && toggleLinkUnderline(this.editor)) {
      return true;
    }
    return toggleFormat(this.editor, format, !!this.options.inlineStyles);
  }

  setStyle(property: StyleProperty, value: string | null): boolean {
    return this.formattable() && setStyleProperty(this.editor, property, value);
  }

  removeFormat(): boolean {
    return this.formattable() && removeFormat(this.editor);
  }

  setLink(href: string, target: string | null, text?: string): boolean {
    return this.formattable() && setLink(this.editor, href, target, text);
  }

  unsetLink(): boolean {
    return this.formattable() && unsetLink(this.editor);
  }

  insertText(text: string): void {
    if (this.options.plainText && !this.options.multiline) {
      text = text.replace(/[\r\n]+/g, ' ');
    }
    const lines = String(text || '').split(/\r\n?|\n/);
    lines.forEach((line, index) => {
      if (index) {
        this.lineBreak(true);
      }
      if (line) {
        insertText(this.editor, line);
      }
    });
  }

  insertHtml(html: string): void {
    this.editor.chain().focus().insertContent(html, {parseOptions: {preserveWhitespace: false}}).run();
  }

  insertImage(src: string): void {
    this.editor.chain().focus().insertContent({type: 'htmlImage', attrs: {attributes: {src, alt: ''}}}).run();
  }

  undo(): void {
    this.editor.commands.undo();
  }

  redo(): void {
    this.editor.commands.redo();
  }

  canUndo(): boolean {
    return this.editor.can().undo();
  }

  canRedo(): boolean {
    return this.editor.can().redo();
  }

  /** Lists, indent and headings, in a paragraph-based text. */
  get commands() {
    return this.editor.chain().focus();
  }

  destroy(): void {
    this.editor.destroy();
  }

  private formattable(): boolean {
    return this.editor.isEditable && !this.options.plainText;
  }

  /** Enter: a line break in an inline text (or anywhere with Shift); in plain one-line text, nothing. */
  private lineBreak(shift = false): boolean {
    if (this.options.plainText && !this.options.multiline) {
      return true;
    }
    if (this.options.shape === 'blocks' && !shift) {
      return false;
    }
    return this.editor.commands.setHardBreak();
  }

  /** Paste into an inline text: each paragraph (or other block) ends with a line break. */
  private pastedHtml(html: string): string {
    if (this.options.shape !== 'inline') {
      return html;
    }
    return html
      .replace(/<\/(p|div|li|h[1-6]|pre|blockquote|tr)>\s*(?=\S)/gi, '<br>')
      .replace(/<\/?(p|div|ul|ol|li|h[1-6]|pre|blockquote|table|tbody|thead|tr|td|th)(\s[^>]*)?>/gi, '');
  }

  private pastePlainText(event: ClipboardEvent): boolean {
    if (!this.options.plainText) {
      return false;
    }
    const text = event.clipboardData?.getData('text/plain') || '';
    this.insertText(text);
    return true;
  }
}

/**
 * The share page's plain-text mode, which turns a text into plain text and back: text with its
 * line breaks (`<br>`, block ends) as newlines, entities decoded, non-breaking spaces as spaces; a
 * one-line text has every line break as a space. (Moved here from share-campaign, which used it with
 * CKEditor.)
 */
export const htmlToPlainText = (html: string, multiline: boolean): string => {
  const markup = String(html || '')
    .replace(/\r\n?/g, '\n')
    .replace(/>\s*\n\s*</g, '><')
    .replace(/<br\s*\/?>[ \t]*\n[ \t]*/gi, '<br />')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]*>/g, '');
  let text = markup;
  if (markup.includes('&') && typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = markup;
    text = textarea.value;
  }
  text = text.replace(/ /g, ' ');
  return multiline ? text : text.replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
};

export const plainTextToHtml = (text: string, multiline: boolean): string => {
  const plain = multiline ? String(text || '') : String(text || '').replace(/[\r\n]+/g, ' ');
  return escapeHtmlText(plain).replace(/\r\n?|\n/g, '<br />');
};
